/* Legacy Storefront Catalog — recovered from April 2026 git history.
 * Original file was /app/frontend/src/pages/storefront/StorefrontCatalog.jsx
 * Removed in commit 8c79a2e (Apr 29, 2026) when tenant/subdomain shell was refactored.
 *
 * Restored here as a STATIC preview with mock data so the user can compare
 * the old layout against the 4 new templates being designed. */
import { useCatalogFonts } from "./_fonts";
import { PreviewRibbon } from "./LinesheetPreview";
import { Package, Plus, ShoppingCart } from "lucide-react";

const ST_BG = "#0a1f1c";        // dark ink
const ST_ACCENT = "#e8ff3a";    // verqo lime
const TENANT_NAME = "Mira Botanicals";

const SAMPLE = [
  { id: 1, name: "Hydrating Face Serum 30ml",   moq: 12, price: 22.50, image: "https://images.unsplash.com/photo-1739980213756-753aea153bb8?w=600" },
  { id: 2, name: "Rose Water Toner 100ml",      moq: 24, price: 14.00, image: "https://images.unsplash.com/photo-1608571424266-edeb9bbefdec?w=600" },
  { id: 3, name: "Vitamin C Brightening Drops", moq: 12, price: 28.00, image: "https://images.unsplash.com/photo-1739980104488-408eff709fff?w=600" },
  { id: 4, name: "Clay Detox Mask Trio",        moq: 18, price: 16.50, image: "https://images.unsplash.com/photo-1739980213756-753aea153bb8?w=600" },
  { id: 5, name: "Restoring Night Oil",         moq: 12, price: 32.00, image: "https://images.unsplash.com/photo-1608571424266-edeb9bbefdec?w=600" },
  { id: 6, name: "Botanical Body Polish",       moq: 24, price: 11.20, image: "https://images.unsplash.com/photo-1739980104488-408eff709fff?w=600" },
  { id: 7, name: "Cooling Aloe Mist",           moq: 24, price: 9.80,  image: "https://images.unsplash.com/photo-1739980213756-753aea153bb8?w=600" },
  { id: 8, name: "Argan Hair Treatment",        moq: 12, price: 24.00, image: "https://images.unsplash.com/photo-1608571424266-edeb9bbefdec?w=600" },
];

export default function LegacyStorefrontCatalogPreview() {
  useCatalogFonts();
  return (
    <div style={{ background: "#fafafa", minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif", color: "#0a1f1c" }} data-testid="legacy-storefront-preview">
      <PreviewRibbon name="Legacy Storefront (April 2026)"/>

      <div className="max-w-7xl mx-auto px-6 py-10" style={{
        // CSS vars used by the original component
        ["--st-bg"]: ST_BG,
        ["--st-accent"]: ST_ACCENT,
      }}>
        {/* Seller-branded hero */}
        <div
          className="relative rounded-3xl p-10 lg:p-14 overflow-hidden"
          style={{ background: ST_BG, color: "#fff" }}
        >
          {/* Subtle grid overlay */}
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage: `linear-gradient(${ST_ACCENT} 1px, transparent 1px), linear-gradient(90deg, ${ST_ACCENT} 1px, transparent 1px)`,
              backgroundSize: "24px 24px",
            }}
          />
          {/* Glow blob */}
          <div
            className="absolute -right-20 -bottom-20 w-[320px] h-[320px] rounded-full blur-3xl"
            style={{ background: ST_ACCENT, opacity: 0.18 }}
          />
          <div className="relative max-w-xl">
            <div className="text-[10.5px] uppercase tracking-[0.22em]" style={{ color: ST_ACCENT, fontFamily: "'JetBrains Mono', monospace" }}>
              Wholesale · Invitation-only
            </div>
            <h1
              className="mt-3 leading-[1.05] tracking-tight"
              style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: "clamp(36px, 4.5vw, 56px)", fontWeight: 500 }}
            >
              {TENANT_NAME} · Wholesale
            </h1>
            <p className="text-white/70 mt-4 text-[15px] leading-relaxed">
              Private linesheet for approved buyers. Pricing, terms and stock visible only to your verified accounts.
            </p>
            <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 ring-1 ring-white/15">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: ST_ACCENT }}/>
              <span className="text-[13px] text-white/80">
                Your credit line: <strong className="text-white">$25,000</strong> · Net-30
              </span>
            </div>
          </div>
        </div>

        {/* Product grid */}
        <div className="mt-10">
          <h2 className="tracking-tight" style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 28, fontWeight: 500 }}>
            Shop wholesale
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="storefront-products">
            {SAMPLE.map((p) => (
              <div
                key={p.id}
                className="rounded-xl border bg-white overflow-hidden group transition-all"
                style={{ borderColor: "rgba(10,31,28,0.10)" }}
              >
                <div className="aspect-[4/3] overflow-hidden" style={{ background: "#f4f1ea" }}>
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"/>
                  ) : (
                    <div className="w-full h-full grid place-items-center" style={{ color: "rgba(10,31,28,0.20)" }}><Package className="w-8 h-8"/></div>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-[14px] font-medium line-clamp-1">{p.name}</div>
                  <div className="text-[11.5px] mt-0.5" style={{ color: "rgba(10,31,28,0.55)" }}>MOQ: {p.moq}</div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="tracking-tight" style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 18 }}>
                      ${p.price.toLocaleString()}
                    </div>
                    <button
                      className="rounded-full h-8 px-3 text-[12px] inline-flex items-center font-medium"
                      style={{ background: ST_BG, color: ST_ACCENT }}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1"/>Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer CTA to cart */}
        <div className="mt-12 rounded-2xl border p-6 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "rgba(10,31,28,0.10)", background: "rgba(10,31,28,0.04)" }}>
          <div>
            <div className="tracking-tight" style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 20 }}>Ready to place an order?</div>
            <div className="text-[13px] mt-0.5" style={{ color: "rgba(10,31,28,0.60)" }}>Review your cart, confirm PO, get live shipping rates.</div>
          </div>
          <button className="rounded-full h-10 px-5 text-[13px] inline-flex items-center gap-2 font-medium" style={{ background: ST_BG, color: ST_ACCENT }}>
            <ShoppingCart className="w-3.5 h-3.5"/>Review cart
          </button>
        </div>

        {/* Recovery note */}
        <div className="mt-10 rounded-xl p-5 text-[12.5px] leading-relaxed" style={{ background: "#fffbea", border: "1px solid #f4dca0", color: "#5b4910" }}>
          <strong>About this file:</strong> recovered from git history (commit <code style={{ fontFamily: "'JetBrains Mono', monospace" }}>882a32b</code>,
          original path <code style={{ fontFamily: "'JetBrains Mono', monospace" }}>frontend/src/pages/storefront/StorefrontCatalog.jsx</code>).
          It was deleted on <strong>Apr 29, 2026</strong> in commit <code style={{ fontFamily: "'JetBrains Mono', monospace" }}>8c79a2e</code> when the
          tenant subdomain shell (<code style={{ fontFamily: "'JetBrains Mono', monospace" }}>/s/:slug/...</code>) was refactored away.
          This is the design you remembered. Use it as a reference for the new Catalog app templates.
        </div>
      </div>
    </div>
  );
}
